<script setup lang="ts">
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputNumber, { InputNumberInputEvent } from 'primevue/inputnumber';
import { useField } from 'vee-validate';
import {
  computed,
  inject,
  nextTick,
  onMounted,
  reactive,
  ref,
  shallowRef,
  watch,
} from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';

import type {
  InputNumberEmits,
  InputNumberEvent,
  InputNumberProps,
  InputNumberSlots,
} from './InputNumber.vue.d';
import FieldWrapper from '../fieldwrapper/FieldWrapper.vue';
import { FieldValidation } from '../form/Form.vue.d';
import Icon from '../icon/Icon.vue';
import InputGroup from '../inputgroup/InputGroup.vue';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

const props = withDefaults(defineProps<InputNumberProps>(), {
  maxDigit: 16,
  padStart: 0,
  showValidatorMessage: true,
  allowEmptyValue: true,
  addonVariant: 'filled',
  minFractionDigits: 0,
  maxFractionDigits: 2,
});

const emit = defineEmits<InputNumberEmits>();
const slots = defineSlots<InputNumberSlots>();

const InputGroupAddonPreset = inject<Record<string, any>>(
  'preset',
  {},
).inputgroupaddon;

const { locale } = useWangsVueConfig('InputNumber');
const Preset = inject<Record<string, any>>('preset', {}).inputnumber;

const inputKey = shallowRef<number>(0);
const inputNumber = ref<InputNumber>();

const field = reactive<FieldValidation<number | undefined>>({
  value: props.value ?? props.modelValue,
});

const inputPlaceholder = computed(
  () =>
    props.placeholder ??
    locale.defaultPlaceholder?.replace(
      '{label}',
      props.label ? props.label?.toLowerCase() : '',
    ),
);

onMounted(() => {
  if (props.useValidator) {
    Object.assign(
      field,
      useField(
        props.fieldName ?? 'numberInput',
        (value: number | undefined) => {
          return nextTick(() => setValidatorMessage(value));
        },
      ),
    );
  }

  const initialValue = props.value ?? props.modelValue;
  const minOrMaxValue =
    initialValue != undefined ? findMinOrMaxValue(initialValue) : initialValue;

  if (minOrMaxValue != null && props.allowEmptyValue)
    field.value = minOrMaxValue;
  else if (!props.allowEmptyValue)
    field.value = minOrMaxValue ?? props.min ?? 0; // Falls back to Zero or Min value when the empty value not allowed

  if (props.invalid && props.useValidator) field.validate();
});

const invalidInput = computed(() => props.invalid || !!field.errorMessage);

const inputNumberId = computed(() => {
  if (props.inputId) return props.inputId;

  return (
    (props.label ?? props.fieldName ?? 'input-number')
      .toLowerCase()
      .trim()
      .replaceAll(' ', '-') + '-inputnumber'
  );
});

const setValidatorMessage = (value: number | undefined): string | boolean => {
  /**
   * Check whether input has value, this way it can also accept the number 0 as value
   */
  const hasValue = value != null && typeof value === 'number';

  if (props.invalid && typeof props.validatorMessage === 'string') {
    return props.validatorMessage;
  } else if (typeof props.validatorMessage !== 'string') {
    const { empty, exist } = props.validatorMessage ?? {};

    if (value == null && props.mandatory) {
      return empty ?? locale.emptyInvalidText.replace('{label}', props.label);
    } else if (hasValue && props.existingValues?.includes(value)) {
      return (
        exist ?? locale.alreadyExistInvalidText?.replace('{label}', props.label)
      );
    }
  }

  return true;
};

const blurAndReFocus = (): void => {
  inputKey.value++;

  nextTick(() => {
    const inputWrapper = inputNumber.value?.[
      '$el' as keyof InputNumber
    ] as HTMLSpanElement;

    const input: HTMLInputElement | null = inputWrapper?.querySelector('input');

    input?.focus();
  });
};

const onUpdateValue = (
  event: InputNumberInputEvent | number,
  type: 'modelValue' | 'input',
): void => {
  const eventValue =
    type === 'input' ? (event as InputNumberInputEvent).value : event;

  const assignEventValue = (): InputNumberEvent => {
    if (eventValue != null) {
      return eventValue !== '-' ? +eventValue : eventValue;
    }

    return undefined;
  };

  let value: InputNumberEvent = assignEventValue();

  if (value != null && !isNaN(+value)) {
    value = eventValue != null ? +eventValue : undefined;
    handleNumberValue(type, value);
  } else if (value == null && typeof value !== 'string') {
    /*
     * When the value is empty and empty value is not allowed,
     * falls back to min value if exists, zero otherwise
     */
    const emptyOrMinValue = !props.allowEmptyValue
      ? (value ?? props.min ?? 0)
      : value;

    field.value = emptyOrMinValue;

    if (type === 'input') {
      if (!props.allowEmptyValue && inputNumber.value) {
        blurAndReFocus();
      }

      emit('input', emptyOrMinValue);
    } else {
      emit('update:modelValue', emptyOrMinValue);
    }
  }
};

const handleNumberValue = (
  type: 'modelValue' | 'input',
  value?: number,
): void => {
  const isExceed = typeof value === 'number' ? isExceedMaxDigits(value) : false;

  let slicedValue: number | string | undefined = value
    ?.toString()
    .slice(0, value < 0 ? props.maxDigit + 1 : props.maxDigit);

  if (slicedValue != null) {
    slicedValue = +(slicedValue ?? 0);
  }

  field.value = isExceed ? +(slicedValue ?? 0) : (value as number | undefined);

  if (isExceed) blurAndReFocus();

  if (type === 'input') {
    emit('input', isExceed ? +(slicedValue ?? 0) : value);
  } else {
    emit('update:modelValue', isExceed ? +(slicedValue ?? 0) : value);
  }
};

const isExceedMaxDigits = (value: number | undefined): boolean => {
  return (
    value != null &&
    value.toString().length > (props.maxDigit > 16 ? 16 : props.maxDigit)
  );
};

const increment = (): void => {
  const incremented = +(field.value ?? 0) + 1;
  if ((props.max && props.max >= incremented) || props.max == null)
    onUpdateValue(incremented, 'modelValue');
};

const decrement = (): void => {
  const decremented = +(field.value ?? 0) - 1;
  if ((props.min && props.min <= decremented) || props.min == null)
    onUpdateValue(decremented, 'modelValue');
};

/**
 *
 * @param value Prevent input have value exceed the range min and max
 */
const findMinOrMaxValue = (value: number): number => {
  if (props.min && value < props.min) return props.min;
  else if (props.max && value > props.max) return props.max;
  return value;
};

/**
 * We need to watch the props.value changes for async data only once after it rendered.
 */
watch(
  () => props.value,
  (value) => {
    const valueInRange = value != null ? findMinOrMaxValue(value) : value;
    if (!isExceedMaxDigits(valueInRange)) {
      field.value = valueInRange;
    }
  },
  { once: true },
);

watch(
  () => props.modelValue,
  (model) => {
    const valueInRange = model != null ? findMinOrMaxValue(model) : model;
    if (!isExceedMaxDigits(valueInRange)) {
      field.value = valueInRange;
    }
  },
);
</script>

<template>
  <FieldWrapper
    :info="fieldInfo"
    :label="props.label"
    :mandatory="props.mandatory"
    :tooltip-pos="tooltipPos"
  >
    <template #label-addon v-if="slots['field-label-addon']">
      <slot name="field-label-addon" />
    </template>

    <InputGroup
      :class="[
        'grid ',
        {
          'grid-cols-[max-content,1fr,max-content]':
            (slots['addon-left'] && slots['addon-right']) || showButtons,
          'grid-cols-[1fr,max-content]':
            !slots['addon-left'] && slots['addon-right'],
          'grid-cols-[max-content,1fr]':
            slots['addon-left'] && !slots['addon-right'],
          'w-max': autoResize,
        },
        inputGroupClass,
      ]"
      :disabled="props.disabled"
      :invalid="invalidInput"
    >
      <InputGroupAddon
        v-if="slots['addon-left'] || props.showButtons"
        :class="[addonLeftClass, { 'cursor-pointer': props.showButtons }]"
        :disabled="disabled"
        :pt="{
          root: InputGroupAddonPreset.root({ props }),
        }"
        @click="props.showButtons && !props.disabled ? decrement() : undefined"
      >
        <slot name="addon-left">
          <Icon v-bind="Preset.decrementicon({ props })" icon="subtract" />
        </slot>
      </InputGroupAddon>

      <span
        :class="['relative', { 'w-max': autoResize }]"
        data-wv-section="autoresizeinputwrapper"
      >
        <span
          v-if="autoResize"
          data-wv-section="inputresizer"
          v-bind="Preset.inputresizer"
        >
          {{ (field.value ?? '').toString().padStart(padStart, '0') }}
        </span>

        <InputNumber
          :key="inputKey"
          ref="inputNumber"
          v-bind="{ ...props, inputNumberClass: undefined }"
          :input-id="inputNumberId"
          :model-value="field.value"
          :placeholder="autoResize ? '' : inputPlaceholder"
          :pt="{
            root: {
              class: [
                Preset.root({ props, parent: $parent }).class,
                inputNumberClass,
                {
                  'absolute left-0 top-0': autoResize,
                },
              ],
            },
          }"
          :show-buttons="false"
          @input="onUpdateValue($event, 'input')"
          @update:model-value="onUpdateValue($event, 'modelValue')"
        />
      </span>

      <InputGroupAddon
        v-if="slots['addon-right'] || props.showButtons"
        :class="[addonRightClass, { 'cursor-pointer': props.showButtons }]"
        :disabled="disabled"
        :pt="{
          root: InputGroupAddonPreset.root({ props }),
        }"
        @click="props.showButtons && !disabled ? increment() : undefined"
      >
        <slot name="addon-right">
          <Icon v-bind="Preset.incrementicon({ props })" icon="add" />
        </slot>
      </InputGroupAddon>
    </InputGroup>

    <slot name="validatormessage">
      <ValidatorMessage
        v-if="props.showValidatorMessage"
        :message="field.errorMessage"
      />
    </slot>
  </FieldWrapper>
</template>
